import { NotificationTypeEnum } from 'api/enums';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import { formatDate, formatDatetime } from 'modules/shared/utils/date-utils';
import { getFileExtension } from 'modules/shared/utils/file-utils';
import moment from 'moment-timezone';
import { FC, ReactNode } from 'react';
import {
    CAMERA_IMAGE_COMMENT_DETAILS,
    CAMERA_IMAGE_COMMENT_REPLY_DETAILS,
    DOCUMENT_DETAILS,
    LIVELAPSE_DETAILS,
    MULTI_IMAGE_DOWNLOAD_DETAILS,
    PROJECT_DETAILS,
} from '../../helpers/notifications.types';
import DocFileIcon from 'modules/shared/components/helpers/DocFileIcon';
import { FileIcon } from 'library/icons';

// else if ([NotificationTypeEnum.SCHEDULED_CAMERA_PROGRESS_REPORT_GENERATED].includes(type)) {
//     const details = props.details as SCHEDULED_CAMERA_PROGRESS_REPORT_GENERATED_DETAILS;
//     console.log(details);
//     // @TODO handle this case
//     return render({
//         icon: <DocFileIcon className="w-7" extension={'pdf'} />,
//         title: 'Phase II - The Villa - Lonavla.pdf',
//         subTitle: 'Jul, 2023',
//     });
// }

interface NotificationFooterProps {
    type: NotificationTypeEnum;
    details: any;
}

const NotificationFooter: FC<NotificationFooterProps> = (props) => {
    const { type } = props;

    const { user } = useAuthSelector();

    const render = (params: { icon: ReactNode; title: string; subTitle: string }) => {
        return (
            <div className="flex items-center gap-3 overflow-hidden mt-3">
                <div className="empty:hidden">{params.icon}</div>
                <div className="overflow-hidden">
                    <div className="font-semibold truncate">{params.title}</div>
                    <div className="text-[11px] text-grayscale-500 truncate">{params.subTitle}</div>
                </div>
            </div>
        );
    };

    if ([NotificationTypeEnum.LIVELAPSE_GENERATED, NotificationTypeEnum.LIVELAPSE_FAILED].includes(type)) {
        const details = props.details as LIVELAPSE_DETAILS;

        return render({
            icon: <DocFileIcon className="w-7" extension={'mp4'} />,
            title: details.filename,
            subTitle: `${details.duration} Day${details.duration !== 1 ? '' : 's'} · ${formatDate(
                details.startDate,
                'DD MMM, YYYY'
            )} to ${formatDate(details.endDate, 'DD MMM, YYYY')}`,
        });
    } else if (
        [
            NotificationTypeEnum.MULTI_IMAGE_DOWNLOAD_GENERATED,
            NotificationTypeEnum.MULTI_IMAGE_DOWNLOAD_GENERATED,
        ].includes(type)
    ) {
        const details = props.details as MULTI_IMAGE_DOWNLOAD_DETAILS;

        return render({
            icon: <DocFileIcon className="w-7" extension={'zip'} />,
            title: details.filename,
            subTitle: `${formatDate(details.startDate, 'DD MMM, YYYY')} to ${formatDate(
                details.endDate,
                'DD MMM, YYYY'
            )}`,
        });
    } else if (
        [NotificationTypeEnum.CAMERA_IMAGE_COMMENT_CREATED, NotificationTypeEnum.CAMERA_IMAGE_COMMENT_TAGGED].includes(
            type
        )
    ) {
        const details = props.details as CAMERA_IMAGE_COMMENT_DETAILS;

        // <Img parentClassName="h-9 aspect-image rounded" className="h-full rounded" src="/dummy-assets/image-1.jpg" />
        // @TODO Add image
        return render({
            icon: null,
            title: `${details.cameraName} - ${details.projectName}`,
            subTitle: formatDatetime(details.cameraImageName.split('.')[0], 'h:mm a [·] DD MMM, YYYY'),
        });
    } else if ([NotificationTypeEnum.CAMERA_IMAGE_COMMENT_REPLIED].includes(type)) {
        const details = props.details as CAMERA_IMAGE_COMMENT_REPLY_DETAILS;

        // @TODO Add image
        return render({
            icon: null,
            title: `${details.cameraName} - ${details.projectName}`,
            subTitle: formatDatetime(details.cameraImageName.split('.')[0], 'h:mm a [·] DD MMM, YYYY'),
        });
    } else if (
        [
            NotificationTypeEnum.PROJECT_CREATED,
            NotificationTypeEnum.PROJECT_ACCESS_GRANTED,
            NotificationTypeEnum.PROJECT_ACCESS_REVOKED,
        ].includes(type)
    ) {
        const details = props.details as PROJECT_DETAILS;

        // <Img parentClassName="h-9 aspect-image rounded" className="h-full rounded" src="https://d19wlcephcto9c.cloudfront.net/v1/project-renders/00222222-1689581638032.jpg" />
        // @TODO Add image
        return render({
            icon: null,
            title: details.projectName,
            subTitle: details.projectLocation.name,
        });
    } else if (
        [NotificationTypeEnum.DOCUMENT_FILE_ACCESS_GRANTED, NotificationTypeEnum.DOCUMENT_FILE_ACCESS_REVOKED].includes(
            type
        )
    ) {
        const details = props.details as DOCUMENT_DETAILS;

        return render({
            icon: <DocFileIcon className="w-7" extension={getFileExtension(details.filePath)} />,
            title: details.fileName,
            subTitle: moment.tz(details.fileCreatedAt, user.preferences.timezone).format('DD MMM, YYYY'),
        });
    } else if (
        [
            NotificationTypeEnum.DOCUMENT_ACCESS_GRANTED,
            NotificationTypeEnum.DOCUMENT_ACCESS_REVOKED,
            NotificationTypeEnum.DOCUMENT_EXTERNAL_LINK_ACCESS_GRANTED,
            NotificationTypeEnum.DOCUMENT_EXTERNAL_LINK_ACCESS_REVOKED,
        ].includes(type)
    ) {
        const details = props.details as DOCUMENT_DETAILS;

        return render({
            icon: null,
            title: details.fileName,
            subTitle: moment.tz(details.fileCreatedAt, user.preferences.timezone).format('DD MMM, YYYY'),
        });
    } else if (NotificationTypeEnum.CUSTOM_PUSH_NOTIFICATION === type) {
        const details = props.details as string;
        return (
            <div className="flex items-center gap-3 overflow-hidden mt-1">
                <div className="overflow-hidden">
                    <div className="text-sm text-grayscale-500 truncate">{details}</div>
                </div>
            </div>
        );
    } else {
        return null;
    }
};

export default NotificationFooter;
